import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BookingArriveByPod } from 'src/app/core/models/transport-operation/booking-arrive-by-pod';
import { ShipmentProgressSearchParams } from 'src/app/core/models/transport-operation/shipment-progress-search-params';
import { environment } from '../../../../environments/environment';
import { mergeDoInfoRequest } from '../components/do-merge/interface/changDoTaskCodeRequest';
import { LicensePlateInfo } from 'src/app/core/models/transport-operation/license-plate-info';
import {QueryOverDCRequest} from '../../../core/models/transport-operation/query-over-dc-request';
import { CreateDoByBBPRequest } from 'src/app/core/models/transport-operation/create-do-request';
import { shippingNotesResponse } from '../components/shipping-notes/interface/shipping-notes-response';
import {MixCartonSearchRequest} from '../../../core/models/transport-operation/mix-carton-search-request';
import { AllocationConstraintRequest } from 'src/app/core/models/transport-operation/query-allocation-constraint-request';
import { AddAllocationConstraintRequest } from 'src/app/core/models/transport-operation/add-allocation-constraint-request';
import { AddAllocationConstraintInboundRequest } from 'src/app/core/models/transport-operation/add-allocation-constraint-inbound-request';
import { AllocationConstraintInboundRequest } from 'src/app/core/models/transport-operation/query-allocation-constraint-inbound-request';


import { PageResp } from 'src/app/core/models/page/page-response';

@Injectable({
  providedIn: 'root'
})
export class DeliveryOrderService {

  constructor(private http: HttpClient) { }

  /**
   * get DO By Filter
   * @param inp UI filter
   */
  getDoByFilter(param: any): Observable<any>{
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/filter`;
    return this.http.post(url, param);
  }

  /**
   * Get all delivery orders.
   */
  getAllDeliveryOrders(): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder`;
    const headers = new HttpHeaders();
    const params = new HttpParams();

    return this.http.get(url, { headers, params });
  }

  /**
   * Get delivery orders for shipping
   * @param param parameters
   */
  getDeliveryOrdersForShipping(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/QueryForShipping`;
    return this.http.post(url, param);
  }

  /**
   * Get recommended forwarder by giving DO number.
   * @param doNo Delivery order number
   */
  getForwarderRecommendation(facility: string, doNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/ForwarderRecommendation/${facility}/${doNo}`;
    return this.http.get(url);
  }

  /**
   * Get Facility Options for DropDown
   */
  getFacilityOptions(): Observable<any> {
    const url = `${environment.logisticsApiUrl}/facility`;
    return this.http.get(url);
  }

  /**
   * Get delivery order by giving DO number.
   * @param doNo Delivery order number.
   */
  getDeliveryOrderByDONo(facility: string, doNo: string, dataScopes: any[]): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/${facility}/${doNo}`;
    const header = new HttpHeaders({'dataScopes': JSON.stringify(dataScopes) });
    return this.http.get(url, {headers: header});
  }

  downloadFile(doc: any): Observable<any> {
    const url = `${environment.dmUrl}/api/v1/documents/${doc.sn}/${doc.version}`;
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  downloadFileAndResponse(doc: any): Observable<any> {
    const url = `${environment.dmUrl}/api/v1/documents/${doc.sn}/${doc.version}`;
    return  this.http.get(url,  {observe: 'response',responseType: 'blob'});
  }

  /**
   * Update drop type & cartonCnt values in delivery orders.
   * @param doNo Delivery order number
   * @param dropType drop type
   * @param cartonCnt carton count
   */
  updateDropTypeAndCartonCntByDONo(doNo: string, dropType: string, cartonCnt: number, totalGrossWeight: number, totalNetWeight: number, volumeWeight: number, userId: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/${doNo}/UpdateDOForDrop`;
    const params =  { dropType, cartonCnt, totalGrossWeight, totalNetWeight, volumeWeight, userId };
    return this.http.patch(url, params);
  }

  /**
   * Update priority by giving delivery order numbers.
   * @param doNos Delivery order numbers
   * @param priority Priority
   */
  updatePriorityByDONos(doNos: string[], priority: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/BatchUpdatePriority`;
    return this.http.patch(url, { doNos, priority });
  }

  /**
   * Update DO status by giving delivery order numbers.
   * @param doNos Delivery order numbers
   * @param priority Priority
   */
  updateStatusByDONos(doNos: string[], status: string, updateUser: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/BatchUpdateStatus`;
    return this.http.patch(url, { doNos, status, updateUser });
  }

  /**
   * Update DO no shipping reason by giving delivery order number.
   * @param doNo Delivery order numbers
   * @param noShippingReason No shipping reason
   * @param noShippingUser user name
   */
  updateNoShippingReasonByDoNo(doNo: string, noShippingReason: string, noShippingUser: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/${doNo}/NoShippingReason`;
    return this.http.patch(url, { noShippingReason, noShippingUser });
  }

  // 上傳 POD (By Booking)
  uploadAndArriveBooking(file: File, jsonObj: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/forwarder/POD-upload/booking`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('json', JSON.stringify(jsonObj));

    return this.http.put(url, formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  // 上傳 POD (By Booking Detail)
  uploadAndArriveDetail(file: File, jsonObj: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/forwarder/POD-upload/delivery-order`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('json', JSON.stringify(jsonObj));

    return this.http.put(url, formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  getProgressResult(params: ShipmentProgressSearchParams): Observable<any>{
    const url = `${environment.logisticsApiUrl}/deliveryOrder/progress/result`;
    return this.http.post(url, params);
  }

  getShipmentProgressDoStatus(doNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/DeliveryOrder/getShipmentProgressDoStatus`
    const headers = new HttpHeaders();
    const params = new HttpParams().append('doNo', doNo);

    return this.http.get(url, {headers, params});
  }

  getDoRealDate(doNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/getDoRealDate`
    const headers = new HttpHeaders();
    const params = new HttpParams().append('doNo', doNo);

    return this.http.get(url, {headers, params});
  }

  changeDoTaskCode(req: mergeDoInfoRequest[]): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/updateMergeDoInfo`;
    return this.http.post(url, req);
  }

  getDoQueryResultExcel(request: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/getDoQueryResultExcel`
    return this.http.post(url, request);
  }

  getDoLockInfo(doNoList: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/lockDoGroupByType`
    return this.http.post(url, doNoList);
  }

  getLicensePlate(doNo: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/getLicensePlateInfo/${doNo}`;
    return this.http.get(url);
  }

  updateDOChangeForCommon(request: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/doChangeForCommon`
    return this.http.post(url, request);
  }

  getDOTenantPermission(): Observable<any> {
    const url = `${environment.logisticsApiUrl}/appCsmSetting/getAppCsmSettingByCsmCode/DOTenantPermission`;
    return this.http.get(url);
  }

  QueryOverDC(request: QueryOverDCRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/getOverDCFromWMS`;
    return this.http.post(url, request);
  }

  exportOverDcData(request: QueryOverDCRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/downloadOverDCFromWMS`;
    return this.http.post(url, request,{ responseType: 'blob'});
  }

  createDoByBBP(request: CreateDoByBBPRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/createDoByCommon`;
    return this.http.post(url, request);
  }

  getCustomerByTenant(tenants: string[]): Observable<any> {
    const url = `${environment.logisticsApiUrl}/customer/getCustomerByTenant`;
    return this.http.post(url, tenants);
  }

  getSpecLocType(): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/getSpecLocType`;
    return this.http.get(url);
  }

  getSpecLocTypeSetting(): Observable<any> {
    const url = `${environment.logisticsApiUrl}/appCsmSetting/getAppCsmSettingByCsmCode/SpecLocType`;
    return this.http.get(url);
  }

  transBBPDoFile(file: any, checkInventoryFlag : boolean, isSkuUppercaseFlag : boolean): Observable<any> {
    let url = `${environment.logisticsApiUrl}/deliveryOrder/transBBPDoFile`;

    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('checkInventoryFlag', checkInventoryFlag.toString());
    formData.append('isSkuUppercaseFlag', isSkuUppercaseFlag.toString());

    let response = this.http.post(url, formData ,  {
      reportProgress: true,
      responseType: 'json'
    } );
    return response;
  }

  queryMixCartonSetting(request: MixCartonSearchRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/queryMixCartonSettingFromWMS`;
    console.log("url",url);
    return this.http.post(url, request);
  }

  queryAllocationConstraint(request: AllocationConstraintRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/getAllocationConstraintFromWMS`;
    return this.http.post(url, request);
  }

  addAllocationConstraint(request: AddAllocationConstraintRequest[]): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/addAllocationConstraintToWMS`;
    return this.http.post(url, request);
  }

  queryAllocationInboundConstraint(request: AllocationConstraintInboundRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/getAllocationConstraintInboundFromWMS`;
    return this.http.post(url, request);
  }

  addAllocationInboundConstraint(request: AddAllocationConstraintInboundRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/addAllocationConstraintInboundToWMS`;
    return this.http.post(url, request);
  }

  exportAllocationConstraint(request: AllocationConstraintRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/downloadAllocationConstraintFromWMS`;
    return this.http.post(url, request,{ responseType: 'blob'});
  }

  exportMixCartonSetting(request: MixCartonSearchRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/downloadMixCartonSettingFromWMS`;
    return this.http.post(url, request,{ responseType: 'blob'});
  }

  getDoShippingNotes(doNo: string, language: string): Observable<shippingNotesResponse> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/getDoShippingNotes/${doNo}/${language}`;
    return this.http.get<shippingNotesResponse>(url);
  }

  searchAsnInfo(params: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/asn/searchAsnInfo`;
    return this.http.post(url, params);
  }

  searchVMIDeliveryOrder(req: any): Observable<PageResp<any>> {
    const url = `${environment.logisticsApiUrl}/deliveryOrder/searchVMIDeliveryOrder`;
    return this.http.post<PageResp<any>>(url, req);
  }
  searchVMIDeliveryOrderDetail(req: any): Observable<any[]> {
      const url = `${environment.logisticsApiUrl}/deliveryOrder/searchVMIDeliveryOrderDetail`;
      return this.http.post<any[]>(url, req);
    }

  uploadVMIDOFile(file: any): Observable<any> {
    let url = `${environment.logisticsApiUrl}/deliveryOrder/uploadVMIDOFile`;

    const formData: FormData = new FormData();
    formData.append('file', file);

    let response = this.http.post(url, formData ,  {
      reportProgress: true,
      responseType: 'json'
    } );
    return response;
  }
}
